import React from 'react';
import { MdOutlineCancel } from 'react-icons/md';
import { FaTrashAlt } from 'react-icons/fa'; // Using a trash icon

/**
 * A reusable modal component for confirming destructive actions like deletion.
 *
 * @param {object} props - The component props.
 * @param {string} [props.title="Are you sure?"] - The main title displayed in the modal.
 * @param {string} [props.message="This action cannot be undone."] - The detailed message for the user.
 * Supports the placeholder {itemName} which is replaced by the itemName prop.
 * @param {string} [props.actionLabel="Delete"] - The label for the confirmation/primary button.
 * @param {function} props.onConfirm - Callback function executed when the user confirms the action.
 * @param {function} props.onCancel - Callback function executed when the user cancels or closes the modal.
 * @param {string} [props.itemName="item"] - The name of the item being deleted, used to personalize the message.
 */
const DeleteConfirmationModal = ({
  title = "Are you sure?",
  message = "This action cannot be undone.",
  actionLabel = "Delete",
  onConfirm,
  onCancel,
  itemName = "item" // Used to personalize the message
}) => {
  // Replace the placeholder {itemName} in the message
  const displayMessage = message.replace('{itemName}', itemName);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Modal Overlay */}
      <div
        className="absolute inset-0 bg-gray-900 bg-opacity-75 transition-opacity duration-300"
        onClick={onCancel}
        aria-hidden="true"
      ></div>

      {/* Modal Content */}
      <div className="relative bg-white rounded-xl shadow-2xl overflow-hidden transform transition-all sm:max-w-md sm:w-full z-10 scale-100 opacity-100">
        <div className="flex justify-end p-2">
            <button
                onClick={onCancel}
                className="p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
                aria-label="Close confirmation modal"
            >
                <MdOutlineCancel size={24} />
            </button>
        </div>
        <div className="px-6 pb-6 pt-0">
          <div className="flex flex-col items-center justify-center text-center">
            {/* Icon */}
            <div className="flex-shrink-0 flex items-center justify-center h-14 w-14 rounded-full bg-red-100">
                <FaTrashAlt className="h-6 w-6 text-red-600" aria-hidden="true" />
            </div>

            {/* Title */}
            <h3 className="mt-4 text-2xl font-bold leading-6 text-gray-900">
              {title}
            </h3>

            {/* Description */}
            <div className="mt-2 text-sm text-gray-500">
              <p>
                {displayMessage}
              </p>
              <p className="font-semibold mt-1 text-red-600">This action cannot be reversed.</p>
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className="bg-gray-50 px-6 py-4 flex flex-col sm:flex-row-reverse sm:gap-4">
          <button
            type="button"
            className="w-full sm:w-auto inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm transition-colors duration-200"
            onClick={onConfirm}
          >
            {actionLabel}
          </button>
          <button
            type="button"
            className="w-full sm:w-auto mt-3 sm:mt-0 inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-200 sm:text-sm transition-colors duration-200"
            onClick={onCancel}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirmationModal;
